import { z } from 'zod';

import { statusEntrySchema, type EnumMap, type StatusMap } from './status';
import { textSchema, type PatternText } from './text';
import type { ScalarOptions } from './values';

/** Presets de coluna (P04): cobrem as 847 colunas do legado sem `cell` escrito à mão. */
export const COLUMN_PRESETS = [
  'text',
  'money',
  'document',
  'date',
  'datetime',
  'percent',
  'number',
  'enum',
  'status',
  'bool',
] as const;
export type ColumnPreset = (typeof COLUMN_PRESETS)[number];

/**
 * Papel da coluna no cartão do iPhone (U41: cartão desenhado, não tabela encolhida): `title` e
 * `subtitle` no topo, `badge` na linha de etiquetas, `amount` em destaque à direita, `meta` no rodapé.
 */
export const MOBILE_ROLES = ['title', 'subtitle', 'badge', 'amount', 'meta', 'hidden'] as const;
export type MobileRole = (typeof MOBILE_ROLES)[number];

export const columnOptionsSchema = z.object({
  unit: z.enum(['cents', 'decimal']).optional(),
  document: z.enum(['auto', 'cpf', 'cnpj']).optional(),
  maxFractionDigits: z.number().int().min(0).max(8).optional(),
  /** `enum`: valor técnico → rótulo (U33). */
  values: z.record(z.string(), textSchema).optional(),
  /** `status`: valor técnico → rótulo, tom e ícone (U19). */
  statuses: z.record(z.string(), statusEntrySchema).optional(),
  /** Campo com o rótulo da API, mostrado só quando o valor técnico não está no mapa. */
  fallbackField: z.string().optional(),
});

/** Coluna serializável: é o que o bloco de configuração guarda (preset por nome, 08 §4.3). */
export const columnSpecSchema = z.object({
  /** Campo da linha, com ponto para aninhado (`owner.name`). Também é o id da coluna. */
  field: z.string().min(1),
  label: textSchema,
  preset: z.enum(COLUMN_PRESETS),
  /** Ordena no servidor por `orderBy=<sortField ?? field>_ASC|DESC`. */
  sortable: z.boolean().optional(),
  sortField: z.string().optional(),
  /** Campo com a dica da célula; ausente mantém o preset original. */
  hintField: z.string().optional(),
  mobile: z.enum(MOBILE_ROLES).optional(),
  pin: z.enum(['left', 'right']).optional(),
  width: z.number().positive().optional(),
  options: columnOptionsSchema.optional(),
});

export type ColumnOptions = z.infer<typeof columnOptionsSchema>;

/** Uma coluna do `DataView`. Sai dos construtores de `columnsFor<Row>()`. */
export interface ColumnSpec {
  readonly pin?: 'left' | 'right' | undefined;
  readonly width?: number | undefined;
  readonly field: string;
  readonly label: PatternText;
  readonly preset: ColumnPreset;
  readonly sortable?: boolean | undefined;
  readonly sortField?: string | undefined;
  readonly hintField?: string | undefined;
  readonly mobile?: MobileRole | undefined;
  readonly options?: ColumnOptions | undefined;
}

interface PresetMeta {
  readonly align: 'start' | 'end';
  readonly mono: boolean;
  readonly mobile: MobileRole;
}

/** Alinhamento, fonte e papel no cartão de cada preset (U39 §11: dinheiro à direita, DM Mono tabular). */
export const PRESET_META: Readonly<Record<ColumnPreset, PresetMeta>> = {
  text: { align: 'start', mono: false, mobile: 'meta' },
  money: { align: 'end', mono: true, mobile: 'amount' },
  document: { align: 'start', mono: true, mobile: 'subtitle' },
  date: { align: 'start', mono: false, mobile: 'meta' },
  datetime: { align: 'start', mono: false, mobile: 'meta' },
  percent: { align: 'end', mono: true, mobile: 'meta' },
  number: { align: 'end', mono: true, mobile: 'meta' },
  enum: { align: 'start', mono: false, mobile: 'meta' },
  status: { align: 'start', mono: false, mobile: 'badge' },
  bool: { align: 'start', mono: false, mobile: 'meta' },
};

/** Papel efetivo no cartão: o declarado, senão o do preset; a primeira coluna de texto vira título. */
export function mobileRoles(columns: readonly ColumnSpec[]): readonly MobileRole[] {
  const hasTitle = columns.some((column) => column.mobile === 'title');
  let titled = hasTitle;
  return columns.map((column) => {
    if (column.mobile) return column.mobile;
    if (!titled && column.preset === 'text') {
      titled = true;
      return 'title';
    }
    return PRESET_META[column.preset].mobile;
  });
}

type Extra = Pick<ColumnSpec, 'sortable' | 'sortField' | 'mobile' | 'pin' | 'width' | 'hintField'>;
type Field<Row> = Extract<keyof Row, string> | `${Extract<keyof Row, string>}.${string}`;

function spec(
  field: string,
  label: PatternText,
  preset: ColumnPreset,
  extra: Extra = {},
  options?: ColumnOptions,
): ColumnSpec {
  return {
    field,
    label,
    preset,
    ...(extra.pin === undefined ? {} : { pin: extra.pin }),
    ...(extra.width === undefined ? {} : { width: extra.width }),
    ...(extra.sortable === undefined ? {} : { sortable: extra.sortable }),
    ...(extra.sortField === undefined ? {} : { sortField: extra.sortField }),
    ...(extra.hintField === undefined ? {} : { hintField: extra.hintField }),
    ...(extra.mobile === undefined ? {} : { mobile: extra.mobile }),
    ...(options && Object.keys(options).length > 0 ? { options } : {}),
  };
}

/**
 * Construtores de coluna tipados pelo campo da linha. Devolvem `ColumnSpec` serializável, então a
 * mesma lista serve à tela e ao bloco de configuração.
 *
 * ```ts
 * const col = columnsFor<FundRow>();
 * const columns = [col.text('name', msg`Nome`), col.document('registrationNumber', msg`CNPJ`), col.money('amount', msg`Valor`)];
 * ```
 */
export function columnsFor<Row>() {
  return {
    text: (field: Field<Row>, label: PatternText, extra?: Extra) =>
      spec(field, label, 'text', extra),
    money: (field: Field<Row>, label: PatternText, extra?: Extra & Pick<ScalarOptions, 'unit'>) =>
      spec(field, label, 'money', extra, extra?.unit ? { unit: extra.unit } : undefined),
    document: (
      field: Field<Row>,
      label: PatternText,
      extra?: Extra & { readonly kind?: 'auto' | 'cpf' | 'cnpj' },
    ) => spec(field, label, 'document', extra, extra?.kind ? { document: extra.kind } : undefined),
    date: (field: Field<Row>, label: PatternText, extra?: Extra) =>
      spec(field, label, 'date', extra),
    datetime: (field: Field<Row>, label: PatternText, extra?: Extra) =>
      spec(field, label, 'datetime', extra),
    percent: (
      field: Field<Row>,
      label: PatternText,
      extra?: Extra & { readonly maxFractionDigits?: number },
    ) =>
      spec(
        field,
        label,
        'percent',
        extra,
        extra?.maxFractionDigits === undefined
          ? undefined
          : { maxFractionDigits: extra.maxFractionDigits },
      ),
    number: (
      field: Field<Row>,
      label: PatternText,
      extra?: Extra & { readonly maxFractionDigits?: number },
    ) =>
      spec(
        field,
        label,
        'number',
        extra,
        extra?.maxFractionDigits === undefined
          ? undefined
          : { maxFractionDigits: extra.maxFractionDigits },
      ),
    enum: (
      field: Field<Row>,
      label: PatternText,
      values: EnumMap,
      extra?: Extra & { readonly fallbackField?: Field<Row> },
    ) =>
      spec(field, label, 'enum', extra, {
        values,
        ...(extra?.fallbackField ? { fallbackField: extra.fallbackField } : {}),
      }),
    status: (
      field: Field<Row>,
      label: PatternText,
      statuses: StatusMap,
      extra?: Extra & { readonly fallbackField?: Field<Row> },
    ) =>
      spec(field, label, 'status', extra, {
        statuses,
        ...(extra?.fallbackField ? { fallbackField: extra.fallbackField } : {}),
      }),
    bool: (field: Field<Row>, label: PatternText, extra?: Extra) =>
      spec(field, label, 'bool', extra),
  };
}

/** Campo usado no `orderBy` da coluna. */
export function sortFieldOf(column: ColumnSpec): string {
  return column.sortField ?? column.field;
}
